import { parseClientPlanContext } from '../clientPlanContext.js';
import { getPlanEntitlements } from '../planStore.js';
import type { BillingCycle, PlanCode, PrototypeRuntime } from '../prototypeTypes.js';
import { showToast } from '../toastController.js';
import { startSessionPoller } from './sessionPoller.js';
import type { ConfirmedSnapshotApplier, PendingChange, SessionSnapshot } from './refreshTypes.js';
import { startVisibilityWatcher } from './visibilityWatcher.js';

const RAPID_POLL_INTERVAL_MS = 3_000;
const RAPID_POLL_ATTEMPTS = 10;
const PENDING_TTL_MS = 35 * 60 * 1_000;

let runtimeRef: PrototypeRuntime | null = null;
let confirmedSnapshotApplier: ConfirmedSnapshotApplier | null = null;
let pendingChange: PendingChange | null = null;
let poller: ReturnType<typeof startSessionPoller> | null = null;
let visibilityCleanup: (() => void) | null = null;
let confirmingKey: string | null = null;

function isBillingCycle(value: unknown): value is BillingCycle {
	return value === 'monthly' || value === 'quarterly' || value === 'annual';
}

function isPlanId(value: unknown): value is Exclude<PlanCode, 'visitor'> {
	return value === 'free' || value === 'lite' || value === 'pro' || value === 'max';
}

function nonemptyString(value: unknown): string | null {
	return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function samePending(left: PendingChange | null, right: PendingChange): boolean {
	return (
		left !== null &&
		left.expectedPlan === right.expectedPlan &&
		left.expectedBillingCycle === right.expectedBillingCycle &&
		left.providerSubscriptionId === right.providerSubscriptionId &&
		left.requestId === right.requestId &&
		left.initiatingUserId === right.initiatingUserId &&
		left.initiatingSessionHash === right.initiatingSessionHash &&
		left.startedAt === right.startedAt
	);
}

function confirmationKey(change: PendingChange): string {
	return [
		change.initiatingUserId,
		change.initiatingSessionHash,
		change.providerSubscriptionId,
		change.requestId ?? '',
		change.expectedPlan,
		change.expectedBillingCycle,
		change.startedAt,
	].join(':');
}

function stopRapidPoll(): void {
	poller?.stop();
	poller = null;
}

function stopVisibilityWatcher(): void {
	visibilityCleanup?.();
	visibilityCleanup = null;
}

function pendingRuntimeIdentityMatches(change: PendingChange, runtime: PrototypeRuntime | null): boolean {
	return runtime?.userId === change.initiatingUserId && runtime.sessionHash === change.initiatingSessionHash;
}

function expireOrInvalidateByRuntime(): boolean {
	const change = pendingChange;
	if (!change) return true;
	if (Date.now() >= change.expiresAt || !pendingRuntimeIdentityMatches(change, runtimeRef)) {
		clearPendingEntitlementChange();
		return true;
	}
	return false;
}

function snapshotInvalidatesPending(snapshot: SessionSnapshot, change: PendingChange): boolean {
	if (snapshot.viewer.authStatus !== 'authenticated') return true;
	if (snapshot.viewer.userId !== change.initiatingUserId || snapshot.viewer.sessionHash !== change.initiatingSessionHash) return true;
	if (snapshot.subscription && snapshot.subscription.providerSubscriptionId !== change.providerSubscriptionId) return true;
	return false;
}

function snapshotConfirmsPending(snapshot: SessionSnapshot, change: PendingChange): boolean {
	return (
		snapshot.viewer.authStatus === 'authenticated' &&
		snapshot.viewer.userId === change.initiatingUserId &&
		snapshot.viewer.sessionHash === change.initiatingSessionHash &&
		snapshot.viewer.billingPlan === change.expectedPlan &&
		snapshot.viewer.planContext?.billingPlan === change.expectedPlan &&
		snapshot.subscription?.providerSubscriptionId === change.providerSubscriptionId &&
		snapshot.subscription.plan === change.expectedPlan &&
		snapshot.subscription.billingCycle === change.expectedBillingCycle
	);
}

function snapshotFailsPending(snapshot: SessionSnapshot, change: PendingChange): boolean {
	if (!change.requestId) return false;
	return snapshot.revisions.some((revision) =>
		revision.requestId === change.requestId
		&& revision.changeType === 'upgrade'
		&& revision.status === 'failed');
}

function presentFailedPending(change: PendingChange): void {
	if (!samePending(pendingChange, change)) return;
	showToast({
		type: 'error',
		message: 'Your upgrade could not be completed. No plan changes were applied. Please try again.',
		durationMs: 6_000,
	});
	clearPendingEntitlementChange();
}

async function fetchAuthoritativeSnapshot(runtime: PrototypeRuntime): Promise<SessionSnapshot> {
	const [session, subscriptionEnvelope] = await Promise.all([runtime.api.getSession(), runtime.api.getCurrentSubscription({ force: true })]);
	if (!session?.viewer) throw new Error('billing_session_unavailable');
	const rawViewer = session.viewer;
	const rawSubscription = subscriptionEnvelope?.subscription;
	const authStatus = rawViewer.authStatus === 'authenticated' ? 'authenticated' : 'visitor';
	const plan = isPlanId(rawViewer.plan) ? rawViewer.plan : null;
	const userId = nonemptyString(rawViewer.userId);
	const sessionHash = nonemptyString(rawViewer.sessionHash);
	const planContext = parseClientPlanContext(rawViewer.planContext);
	const billingPlan = planContext?.billingPlan ?? null;

	let subscription: SessionSnapshot['subscription'] = null;
	const providerSubscriptionId = nonemptyString(rawSubscription?.providerSubscriptionId);
	if (rawSubscription && providerSubscriptionId && isPlanId(rawSubscription.plan) && isBillingCycle(rawSubscription.billingCycle)) {
		subscription = {
			providerSubscriptionId,
			plan: rawSubscription.plan,
			billingCycle: rawSubscription.billingCycle,
			currentPeriodEnd: typeof rawSubscription.currentPeriodEnd === 'string' ? rawSubscription.currentPeriodEnd : null,
			cancelAtPeriodEnd: rawSubscription.cancelAtPeriodEnd === true,
		};
	}
	const revisions = Array.isArray(subscriptionEnvelope?.changes)
		? subscriptionEnvelope.changes.map((revision) => ({
			requestId: nonemptyString(revision?.requestId),
			changeType: typeof revision?.changeType === 'string' ? revision.changeType : '',
			status: typeof revision?.status === 'string' ? revision.status : '',
			toPlan: typeof revision?.toPlan === 'string' ? revision.toPlan : null,
			toBillingCycle: typeof revision?.toBillingCycle === 'string' ? revision.toBillingCycle : null,
			reasonCode: typeof revision?.reasonCode === 'string' ? revision.reasonCode : null,
		}))
		: [];

	return {
		viewer: {
			authStatus,
			plan,
			billingPlan,
			userId,
			sessionHash,
			email: typeof rawViewer.email === 'string' ? rawViewer.email : null,
			displayName: typeof rawViewer.displayName === 'string' ? rawViewer.displayName : null,
			planContext,
		},
		subscription,
		revisions,
	};
}

function billingActivationMessage(plan: Exclude<PlanCode, 'visitor'>, billingCycle: BillingCycle): string {
	const planName = getPlanEntitlements(plan).label;
	const cycleName = billingCycle === 'monthly' ? 'monthly' : billingCycle === 'quarterly' ? 'quarterly' : 'annual';
	return `${planName} ${cycleName} access is now active.`;
}

export function activationMessage(plan: string, privateChaptersPerMonth: number): string {
	if (plan === 'lite') return 'Lite activated - full chapters, audio, video unlocked';
	if (plan === 'pro') return `Pro activated - AI Create + ${privateChaptersPerMonth} chapter quota unlocked`;
	if (plan === 'max') return `Max activated - all features + ${privateChaptersPerMonth} chapter quota unlocked`;
	return `${plan} activated`;
}

async function applyConfirmedSnapshot(snapshot: SessionSnapshot, change: PendingChange): Promise<void> {
	const key = confirmationKey(change);
	if (confirmingKey === key || !samePending(pendingChange, change) || !snapshotConfirmsPending(snapshot, change)) return;
	confirmingKey = key;
	try {
		const applied = (await confirmedSnapshotApplier?.(snapshot, change)) === true;
		if (!applied || !samePending(pendingChange, change)) return;
		showToast({
			type: 'success',
			message: billingActivationMessage(change.expectedPlan as Exclude<PlanCode, 'visitor'>, change.expectedBillingCycle),
			durationMs: 4_000,
		});
		clearPendingEntitlementChange();
	} finally {
		if (confirmingKey === key) confirmingKey = null;
	}
}

async function inspectAuthoritativeState(runtime: PrototypeRuntime, change: PendingChange): Promise<SessionSnapshot | null> {
	if (expireOrInvalidateByRuntime() || !samePending(pendingChange, change)) return null;
	const snapshot = await fetchAuthoritativeSnapshot(runtime);
	if (!samePending(pendingChange, change)) return null;
	if (snapshotInvalidatesPending(snapshot, change)) {
		clearPendingEntitlementChange();
		return null;
	}
	if (snapshotFailsPending(snapshot, change)) {
		presentFailedPending(change);
		return null;
	}
	return snapshotConfirmsPending(snapshot, change) ? snapshot : null;
}

function ensureVisibilityWatcher(runtime: PrototypeRuntime): void {
	if (visibilityCleanup) return;
	visibilityCleanup = startVisibilityWatcher(() => {
		const change = pendingChange;
		if (!change) return;
		void inspectAuthoritativeState(runtime, change)
			.then((snapshot) => snapshot ? applyConfirmedSnapshot(snapshot, change) : undefined)
			.catch(() => undefined);
	});
}

export function setEntitlementRefreshRuntime(runtime: PrototypeRuntime, applier?: ConfirmedSnapshotApplier): void {
	if (pendingChange && !pendingRuntimeIdentityMatches(pendingChange, runtime)) clearPendingEntitlementChange();
	runtimeRef = runtime;
	if (applier !== undefined) confirmedSnapshotApplier = applier;
}

export function markPendingEntitlementChange(
	expectedPlan: Exclude<PlanCode, 'visitor'>,
	expectedBillingCycle: BillingCycle,
	providerSubscriptionId: string,
	requestId?: string,
): PendingChange | null {
	const runtime = runtimeRef;
	const userId = nonemptyString(runtime?.userId);
	const sessionHash = nonemptyString(runtime?.sessionHash);
	const subscriptionId = nonemptyString(providerSubscriptionId);
	if (!runtime || !userId || !sessionHash || !subscriptionId) return null;
	const startedAt = Date.now();
	pendingChange = {
		expectedPlan,
		expectedBillingCycle,
		providerSubscriptionId: subscriptionId,
		requestId: nonemptyString(requestId),
		initiatingUserId: userId,
		initiatingSessionHash: sessionHash,
		startedAt,
		expiresAt: startedAt + PENDING_TTL_MS,
	};
	return pendingChange;
}

export function clearPendingEntitlementChange(): void {
	pendingChange = null;
	stopRapidPoll();
	stopVisibilityWatcher();
}

export function getPendingEntitlementChange(): PendingChange | null {
	return pendingChange;
}

export function startPendingEntitlementPoll(
	runtime: PrototypeRuntime,
	expectedPlan: Exclude<PlanCode, 'visitor'>,
	expectedBillingCycle: BillingCycle,
	providerSubscriptionId: string,
	requestId?: string,
): void {
	stopRapidPoll();
	setEntitlementRefreshRuntime(runtime);
	const change = markPendingEntitlementChange(expectedPlan, expectedBillingCycle, providerSubscriptionId, requestId);
	if (!change) return;
	ensureVisibilityWatcher(runtime);
	poller = startSessionPoller({
		config: { interval: RAPID_POLL_INTERVAL_MS, maxAttempts: RAPID_POLL_ATTEMPTS },
		fetchSession: () => inspectAuthoritativeState(runtime, change),
		check: (snapshot) => snapshotConfirmsPending(snapshot, change),
		onSuccess: (snapshot) => applyConfirmedSnapshot(snapshot, change),
		onTimeout: () => {
			poller = null;
			if (samePending(pendingChange, change)) {
				showToast({
					type: 'info',
					message: 'Your upgrade is still processing. Access will update after payment confirmation.',
					durationMs: 5_000,
				});
			}
		},
	});
}

export async function refreshEntitlementSession(runtime: PrototypeRuntime): Promise<string | null> {
	setEntitlementRefreshRuntime(runtime);
	const change = pendingChange;
	if (!change || expireOrInvalidateByRuntime()) return null;
	try {
		const snapshot = await fetchAuthoritativeSnapshot(runtime);
		if (!samePending(pendingChange, change)) return snapshot.viewer.plan;
		if (snapshotInvalidatesPending(snapshot, change)) {
			clearPendingEntitlementChange();
			return snapshot.viewer.plan;
		}
		if (snapshotFailsPending(snapshot, change)) {
			presentFailedPending(change);
			return snapshot.viewer.plan;
		}
		if (snapshotConfirmsPending(snapshot, change)) await applyConfirmedSnapshot(snapshot, change);
		return snapshot.viewer.plan;
	} catch {
		return null;
	}
}

export function destroyEntitlementRefresh(): void {
	clearPendingEntitlementChange();
	runtimeRef = null;
	confirmedSnapshotApplier = null;
	confirmingKey = null;
}

export const entitlementRefresh = {
	refreshSession: refreshEntitlementSession,
	startPendingPoll: startPendingEntitlementPoll,
	markPending: markPendingEntitlementChange,
	clearPending: clearPendingEntitlementChange,
	stop: stopRapidPoll,
	destroy: destroyEntitlementRefresh,
};
